import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getApiBaseUrlDynamic } from '../config/api';
import toast from 'react-hot-toast';
import { getFCMToken, onMessageListener } from '../firebase-config';

// API konfigürasyonu - LOCAL VE UZAK IP'LER
console.log('🔧 AuthContext: Setting up API configuration with local/remote IPs...');

// Platform tespiti
const isFile = typeof window !== 'undefined' && window.location && window.location.protocol === 'file:';
const isNative = typeof window !== 'undefined' && window.Capacitor && (window.Capacitor.isNativePlatform ? window.Capacitor.isNativePlatform() : !!window.Capacitor.getPlatform);
const isEmulator = typeof window !== 'undefined' && window.navigator && window.navigator.userAgent && 
  (window.navigator.userAgent.includes('Android Emulator') || window.navigator.userAgent.includes('emulator'));

console.log('🔍 Platform detection:', { isFile, isNative, isEmulator });

// Basit requestId üretici
const generateRequestId = () => {
  try {
    const rnd = Math.random().toString(36).slice(2, 10);
    const ts = Date.now().toString(36);
    return `L-${ts}-${rnd}`;
  } catch (_) {
    return `L-${Date.now()}`;
  }
};

// Axios interceptor ekle
axios.interceptors.request.use(
  (config) => {
    console.log('🌐 API Request:', config.method?.toUpperCase(), config.url);
    return config;
  },
  (error) => {
    console.error('❌ API Request Error:', error);
    return Promise.reject(error);
  }
);

// Optional/opsiyonel endpoint'ler - 404 hataları sessizce yakalanacak
const SILENT_404_ENDPOINTS = [
  '/api/notifications/',
  '/api/smart-notifications',
  '/api/ai/',
  '/api/fcm-subscribe',
  '/api/push-subscribe',
  '/api/subscribe',
  '/api/unsubscribe',
  '/api/error-logs',
  '/api/log-error',
  '/api/theme',
  '/api/user-settings',
  '/api/customers/stats',
  '/api/maintenance-contracts/stats',
  '/api/region-maintenance/stats',
  '/api/reports/dashboard-summary',
  '/api/analytics/',
];

// Network Error hataları için sessiz yakalama (backend çalışmıyorsa tekrar tekrar hata gösterme)
let lastNetworkErrorTime = 0;
const NETWORK_ERROR_SILENCE_DURATION = 5000; // 5 saniye boyunca sessiz
let healthCheckErrorCount = 0; // Health check hata sayacı
const MAX_HEALTH_ERROR_LOGS = 2; // İlk 2 health check hatasını log'la, sonrasını sessizce yakala

// 404 hatası bu endpoint'lerden birisi için mi kontrolü
const shouldSilence404 = (url) => {
  if (!url) return false;
  return SILENT_404_ENDPOINTS.some(endpoint => url.includes(endpoint));
};

axios.interceptors.response.use(
  (response) => {
    // Başarılı istekler için sadece development'ta log'la
    if (process.env.NODE_ENV === 'development') {
      console.log('✅ API Response:', response.status, response.config.url);
    }
    // Health check başarılı olduğunda sayacı sıfırla
    if (response.config?.url?.includes('/api/health')) {
      healthCheckErrorCount = 0;
    }
    return response;
  },
  (error) => {
    const status = error.response?.status;
    const url = error.config?.url || error.response?.config?.url || '';
    const isNetworkError = !error.response && (error.code === 'ECONNABORTED' || error.message?.includes('Network Error') || error.message?.includes('timeout'));
    
    // Network Error için sessiz yakalama (backend çalışmıyorsa spam'ı önle)
    if (isNetworkError) {
      const now = Date.now();
      const timeSinceLastError = now - lastNetworkErrorTime;
      const isHealthCheck = url?.includes('/api/health');
      
      // Health check için özel yönetim: İlk birkaç hatayı log'la, sonrasını sessizce yakala
      if (isHealthCheck) {
        healthCheckErrorCount++;
        if (healthCheckErrorCount <= MAX_HEALTH_ERROR_LOGS) {
          console.warn(`⚠️ Health check hatası (${healthCheckErrorCount}/${MAX_HEALTH_ERROR_LOGS}):`, url || '(unknown)');
        }
        // Health check hatalarını sessizce yakala (retry mekanizması zaten var)
        return Promise.reject(error);
      }
      
      if (timeSinceLastError < NETWORK_ERROR_SILENCE_DURATION) {
        // Son hatadan bu yana çok kısa süre geçtiyse sessizce yakala
        return Promise.reject(error);
      }
      
      // İlk Network Error veya yeterli süre geçtiyse log'la (health check hariç)
      lastNetworkErrorTime = now;
      // Sadece development'ta log yap
      if (process.env.NODE_ENV === 'development') {
        console.error('❌ API Error: Network Error', url || '(unknown)');
      }
      return Promise.reject(error);
    }
    
    // 404 hatası ve optional endpoint ise sessizce yakala
    if (status === 404 && shouldSilence404(url)) {
      // Sessizce yakala - log yapma
      return Promise.reject(error);
    }
    
    // Diğer hatalar için log yap
    if (status === 401) {
      // 401 hatası - sadece development'ta logla
      if (process.env.NODE_ENV === 'development') {
        console.warn('⚠️ Unauthorized:', url);
      }
    } else if (status === 403) {
      // 403 hatası - sadece development'ta logla
      if (process.env.NODE_ENV === 'development') {
        console.warn('⚠️ Forbidden:', url);
      }
    } else if (status >= 500) {
      // 5xx hatalar - sunucu hatası - her zaman logla
      console.error('❌ Server Error:', status, url, error.message);
    } else {
      // Diğer 4xx hatalar (400, 405, vb.) - sadece development'ta logla
      if (process.env.NODE_ENV === 'development') {
        console.error('❌ API Error:', status, url, error.message);
      }
    }
    
    return Promise.reject(error);
  }
);

// API URL seçimi - Runtime'da dinamik olarak belirlenir
console.log('📱 Device detection:', { isFile, isNative, isEmulator, userAgent: window.navigator?.userAgent });

// Base URL'i runtime'da dinamik olarak ayarla
const updateAxiosBaseURL = () => {
  const dynamicUrl = getApiBaseUrlDynamic();
  const oldBaseURL = axios.defaults.baseURL;
  axios.defaults.baseURL = dynamicUrl || '';
  
  if (oldBaseURL !== axios.defaults.baseURL) {
    console.log('🔄 Axios Base URL değişti:', oldBaseURL || '(relative)', '->', axios.defaults.baseURL || '(relative)');
  } else {
    console.log('🚀 Axios Base URL:', axios.defaults.baseURL || '(relative)');
  }
};

// İlk ayarla
updateAxiosBaseURL();

// Runtime'da güncelleme için dinleyici - Daha agresif kontrol
if (typeof window !== 'undefined') {
  // Hemen kontrol et
  setTimeout(updateAxiosBaseURL, 50);
  
  // DOMContentLoaded'da kontrol et
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      setTimeout(updateAxiosBaseURL, 100);
    });
  }
  
  // Window load'ta kontrol et
  window.addEventListener('load', () => {
    setTimeout(updateAxiosBaseURL, 200); // Capacitor yüklenmesi için bekleme
  });
  
  // Capacitor hazır olduğunda kontrol et
  if (window.Capacitor) {
    setTimeout(updateAxiosBaseURL, 300);
  }
}

// Her axios isteğinden önce URL'i kontrol et (interceptor)
axios.interceptors.request.use((config) => {
  // Her istekten önce baseURL'i kontrol et ve güncelle
  const currentUrl = getApiBaseUrlDynamic();
  if (config.baseURL !== currentUrl) {
    config.baseURL = currentUrl || '';
    console.log('🔧 Request interceptor - BaseURL güncellendi:', config.baseURL || '(relative)');
  }
  return config;
});

axios.defaults.timeout = 30000; // 30 saniye timeout

// ---------------------------------------------------------------------------
// Token yenileme
//
// Access token artik KISA OMURLU (varsayilan 15 dk). Otomatik yenileme olmadan
// kullanici 15 dakikada bir disari atilirdi. Yenileme, uzun omurlu ve sunucu
// tarafinda iptal edilebilen refresh token ile yapilir.
//
// Ayni anda birden fazla istek 401 alirsa yenilemeyi TEK SEFER calistiriyoruz;
// aksi halde paralel yenilemeler birbirinin token'ini gecersiz kilar (sunucu
// her kullanimda rotate ediyor ve tekrar kullanimi hirsizlik sayiyor).
// ---------------------------------------------------------------------------
let refreshInFlight = null;

// Oturumun ait oldugu kiraci (firma) bilgisi. Coklu kiracili yapida kullanici
// hangi firmada calistigini gorebilmeli (Layout gosteriyor) ve sayfa
// yenilendiginde bu bilgi kaybolmamali.
const KIRACI_ANAHTARI = 'tenant';

export const readStoredTenant = () => {
  try {
    const ham = localStorage.getItem(KIRACI_ANAHTARI);
    return ham ? JSON.parse(ham) : null;
  } catch (_) {
    return null;
  }
};

export const clearStoredSession = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('lastLoginTime');
  localStorage.removeItem(KIRACI_ANAHTARI);
  try { delete axios.defaults.headers.common.Authorization; } catch (_) { /* yoksay */ }
};

export const storeSession = (data) => {
  const accessToken = data.access_token || data.token;
  if (accessToken) {
    localStorage.setItem('token', accessToken);
    axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
  }
  if (data.refresh_token) {
    localStorage.setItem('refreshToken', data.refresh_token);
  }
  // Kiraci bilgisi yalnizca oturum acan yanitlarda gelir; token yenilemede
  // gelmedigi icin var olani EZMIYORUZ.
  if (data.tenant && data.tenant.slug) {
    localStorage.setItem(KIRACI_ANAHTARI, JSON.stringify(data.tenant));
  }
  return accessToken;
};

async function runTokenRefresh() {
  if (refreshInFlight) return refreshInFlight;

  const stored = localStorage.getItem('refreshToken');
  if (!stored) {
    clearStoredSession();
    throw new Error('Yenileme anahtarı yok');
  }

  refreshInFlight = axios
    .post('/api/auth/refresh', { refresh_token: stored }, { skipAuthRefresh: true })
    .then((response) => storeSession(response.data))
    .catch((error) => {
      // Yenileme basarisiz: oturum gercekten bitmis (veya iptal edilmis)
      clearStoredSession();
      throw error;
    })
    .finally(() => {
      refreshInFlight = null;
    });

  return refreshInFlight;
}

// ---------------------------------------------------------------------------
// Abonelik / kota yonlendirmesi
//
// 402 (kota) ve 403 TENANT_SUSPENDED durumlarinda kullaniciyi odeme ekranina
// goturuyoruz. Bir sayfada ayni anda birden fazla istek hata alabildigi icin
// yonlendirmeyi TEK SEFER tetikliyoruz; aksi halde art arda gelen
// window.location atamalari sayfayi kilitliyor.
// ---------------------------------------------------------------------------
let abonelikYonlendirmesiBekliyor = false;

const ABONELIK_SAYFASI = '/subscription';

// Kayit/davet/giris uclari oturum ACILMADAN once cagrilir; buradan gelen 402'de
// korumali /subscription sayfasina atmak kullaniciyi /login'e dusurur.
const YONLENDIRMESIZ_ONEKLER = ['/api/signup', '/api/auth', '/api/public'];

const yonlendirmeUygunMu = (url) => {
  const yol = String(url || '');
  return !YONLENDIRMESIZ_ONEKLER.some((onek) => yol.includes(onek));
};

// Response interceptor bu durum kodlarini KENDISI toast'liyor; cagiran yerde
// tekrar gostermeyelim (ayni mesaj iki kez ucuyordu).
const interceptorZatenBildirdiMi = (durum) => [402, 403, 422].includes(durum);

const abonelikSayfasinaGit = () => {
  if (abonelikYonlendirmesiBekliyor) return;
  if (window.location.pathname.startsWith(ABONELIK_SAYFASI)) return;
  abonelikYonlendirmesiBekliyor = true;
  // Kisa gecikme: toast okunabilsin, sonra sayfa degissin.
  setTimeout(() => {
    window.location.href = ABONELIK_SAYFASI;
  }, 1500);
};

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  // Oturumun ait oldugu firma. Sayfa yenilendiginde /api/auth/me kiraci bilgisi
  // dondurmedigi icin ilk degeri localStorage'dan okuyoruz.
  const [tenant, setTenant] = useState(() => readStoredTenant());
  // Sizinti nedeniyle zorunlu sifre degisikligi bayragi (bkz. force-password-reset)
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [loading, setLoading] = useState(true);

  const urlBase64ToUint8Array = useCallback((base64String) => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }, []);

  const ensurePushSubscription = useCallback(async () => {
    try {
      // Platform kontrolü
      const isFileProtocol = typeof window !== 'undefined' && window.location && window.location.protocol === 'file:';
      const isNative = typeof window !== 'undefined' && window.Capacitor && (window.Capacitor.isNativePlatform ? window.Capacitor.isNativePlatform() : !!window.Capacitor.getPlatform);
      
      // Token kontrolü
      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('Token bulunamadı');
        return false;
      }

      // Android/APK için FCM kullan
      if (isFileProtocol || isNative) {
        console.log('📱 Android/APK/Emulator detected, using FCM');
        
        // FCM token al
        const fcmToken = await getFCMToken();
        if (!fcmToken) {
          console.warn('FCM token alınamadı');
          return false;
        }

        // Sunucuya FCM token'ı kaydet
        try {
          console.log('🔔 FCM token kaydediliyor:', fcmToken.substring(0, 20) + '...');
          await axios.post('/api/notifications/fcm-subscribe', {
            fcmToken,
            platform: 'android'
          });
          console.log('✅ FCM token başarıyla kaydedildi');
          return true;
        } catch (error) {
          console.error('FCM token kaydetme hatası:', error);
          return false;
        }
      }

      // Web için Web Push API kullan
      if (process.env.NODE_ENV !== 'production') {
        console.debug('Push subscription skipped in development (SW devre dışı).');
        return true;
      }
      
      // Tarayıcı desteği kontrolü
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        console.warn('Tarayıcı push notification desteklemiyor');
        return false;
      }
      
      // İzin kontrolü
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        console.warn('Bildirim izni verilmedi:', permission);
        toast.error('Bildirim izni verilmedi');
        return false;
      }
      
      // Service Worker kayıt
      const reg = (await navigator.serviceWorker.getRegistration()) || (await navigator.serviceWorker.register('/firebase-messaging-sw.js'));
      
      // VAPID public key alma
      const { data } = await axios.get('/api/notifications/vapid-public-key');
      const publicKey = data?.publicKey;
      if (!publicKey) {
        console.error('VAPID public key bulunamadı:', data);
        toast.error('Sunucuda VAPID anahtarı bulunamadı');
        return false;
      }
      
      // Mevcut aboneliği kontrol et ve kaldır
      let sub = await reg.pushManager.getSubscription();
      if (sub) {
        try { 
          await sub.unsubscribe(); 
        } catch (e) {
          console.warn('Abonelik kaldırma hatası:', e);
        }
      }
      
      // Yeni abonelik oluştur
      sub = await reg.pushManager.subscribe({ 
        userVisibleOnly: true, 
        applicationServerKey: urlBase64ToUint8Array(publicKey) 
      });
      
      // Sunucuya aboneliği kaydet
      await axios.post('/api/notifications/subscribe', sub);
      
      toast.success('Web bildirimleri etkinleştirildi');
      return true;
    } catch (err) {
      console.error('Push subscription failed:', err);
      let errorMessage = 'Bilinmeyen hata';
      
      if (err.name === 'NotAllowedError') {
        errorMessage = 'Bildirim izni reddedildi';
      } else if (err.name === 'NotSupportedError') {
        errorMessage = 'Push notification desteklenmiyor';
      } else if (err.name === 'InvalidStateError') {
        errorMessage = 'Geçersiz durum hatası';
      } else if (err.name === 'NetworkError') {
        errorMessage = 'Ağ bağlantı hatası';
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      toast.error(`Bildirimler etkinleştirilemedi: ${errorMessage}`);
      return false;
    }
  }, [urlBase64ToUint8Array]);

  // Axios interceptor for handling 401 errors and adding auth token
  useEffect(() => {
    // Request interceptor - add token to all requests
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        console.log('Request interceptor - Token:', token ? 'Present' : 'Missing');
        console.log('Request URL:', config.url);
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor - handle 401 errors (logout). 403 sadece uyarı.
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        console.log('Response interceptor - Error:', error.response?.status, error.response?.data);

        const durum = error.response?.status;
        const veri = error.response?.data || {};
        const istekUrl = error.config?.url || '';

        // 402: plan/kota engeli. Kullanici oturumunu KAPATMIYORUZ — sorunu
        // ancak abonelik sayfasindan cozebilir.
        if (durum === 402) {
          toast.error(veri.error || 'Plan sınırınıza ulaştınız.');
          // FEATURE_NOT_IN_PLAN plan yukseltme gerektirir ama kullanici o an
          // baska bir is yapiyor olabilir; sadece bilgilendiriyoruz.
          if (veri.code !== 'FEATURE_NOT_IN_PLAN' && yonlendirmeUygunMu(istekUrl)) {
            abonelikSayfasinaGit();
          }
          return Promise.reject(error);
        }

        // Askidaki kiraci: oturum GECERLI, sadece erisim kisitli. Cikis
        // yaptirirsak kullanici odeme yapamaz.
        if (durum === 403 && veri.code === 'TENANT_SUSPENDED') {
          toast.error(veri.error || 'Aboneliğiniz askıda. Ödeme için abonelik sayfasına gidin.');
          if (yonlendirmeUygunMu(istekUrl)) {
            abonelikSayfasinaGit();
          }
          return Promise.reject(error);
        }

        // E-fatura icin firma kimlik bilgileri eksik: kullaniciya nereye
        // gidecegini soyluyoruz, sayfayi zorla degistirmiyoruz (form doldurmus
        // olabilir).
        if (durum === 422 && /Firma VKN/i.test(veri.error || '')) {
          toast.error(`${veri.error} (Ayarlar > Parametreler)`, { duration: 6000 });
          return Promise.reject(error);
        }

        if (error.response?.status === 403) {
          // Sunucu sifre yenilemesi zorunlu diyorsa zorunlu ekrani ac
          if (error.response?.data?.code === 'PASSWORD_CHANGE_REQUIRED') {
            setMustChangePassword(true);
            return Promise.reject(error);
          }
          // 403: Yetkisiz işlem. Token hatası ise token temizle ve login iste.
          const msg = error.response?.data?.error || 'Bu işlem için yetkiniz yok';
          const details = error.response?.data?.details || '';
          const isTokenInvalid = /token/i.test(msg) || /invalid signature/i.test(details || msg);
          if (isTokenInvalid) {
            localStorage.removeItem('token');
            localStorage.removeItem('lastLoginTime');
            setUser(null);
            toast.error('Oturum geçersiz. Lütfen tekrar giriş yapın.');
          } else {
            toast.error(msg);
          }
          return Promise.reject(error);
        }
        if (error.response?.status === 401) {
          // Login sonrası 5 saniye içindeyse logout yapma (token henüz işleniyor olabilir)
          const storedLoginTime = localStorage.getItem('lastLoginTime');
          const loginTime = storedLoginTime ? parseInt(storedLoginTime) : null;
          const timeSinceLogin = loginTime ? Date.now() - loginTime : Infinity;
          const isRecentLogin = timeSinceLogin < 5000; // 5 saniye
          
          console.warn('⚠️  Auth hatası tespit edildi:', {
            status: error.response?.status,
            error: error.response?.data?.error,
            url: error.config?.url,
            timeSinceLogin: timeSinceLogin < Infinity ? `${Math.round(timeSinceLogin / 1000)}s` : 'N/A',
            isRecentLogin
          });
          
          // Eğer çok yeni login ise ve login endpoint'i değilse, logout yapma
          if (isRecentLogin && !error.config?.url?.includes('/api/auth/login')) {
            console.log('⚠️  Login sonrası kısa süre içinde auth hatası, logout yapılmıyor...');
            return Promise.reject(error);
          }

          const original = error.config || {};
          const isAuthEndpoint = /\/api\/auth\/(login|refresh)/.test(original.url || '');

          // Access token'in suresi dolmus olabilir: BIR KEZ yenileyip istegi
          // tekrarla. Yenileme basarisiz olursa oturum gercekten bitmistir.
          if (!isAuthEndpoint && !original.skipAuthRefresh && !original._retriedAfterRefresh) {
            original._retriedAfterRefresh = true;
            try {
              const yeniToken = await runTokenRefresh();
              original.headers = { ...(original.headers || {}), Authorization: `Bearer ${yeniToken}` };
              return axios(original);
            } catch (_) {
              // asagidaki cikis akisina dus
            }
          }

          // 401: Login sayfasında değilsek oturumu temizle
          if (!window.location.pathname.includes('/login')) {
            console.log('Oturum sonlandırılıyor...');
            clearStoredSession();
            setUser(null);
            setTenant(null);

            if (error.response?.data?.code === 'REFRESH_TOKEN_REUSED') {
              toast.error('Güvenlik nedeniyle oturumunuz sonlandırıldı. Lütfen tekrar giriş yapın.');
            } else if (window.location.pathname.includes('/technical-assistant')) {
              toast.error('Oturum süreniz doldu. Lütfen tekrar giriş yapın.');
            }
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  // Check authentication - ANDROID CİHAZ İÇİN ZORLA LOGIN
  // Acilista oturumu geri yukle.
  //
  // Onceden burada oturum kontrolu HIC YAPILMIYORDU (yalnizca 1 sn sonra
  // loading=false); saklanan token doğrulanmadigi icin kullanici sayfayi her
  // yenilediginde login ekranina dusuyordu. Artik token varsa dogrulaniyor,
  // suresi dolmussa refresh token ile sessizce yenileniyor.
  useEffect(() => {
    let iptal = false;

    const oturumuGeriYukle = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        if (!iptal) setLoading(false);
        return;
      }

      axios.defaults.headers.common.Authorization = `Bearer ${token}`;

      try {
        const response = await axios.get('/api/auth/me');
        if (!iptal && response.data?.user) {
          setUser(response.data.user);
        }
      } catch (error) {
        // 401 ise response interceptor zaten yenilemeyi denedi ve basarisiz oldu
        if (!iptal) {
          clearStoredSession();
          setUser(null);
          setTenant(null);
        }
      } finally {
        if (!iptal) setLoading(false);
      }
    };

    oturumuGeriYukle();
    return () => { iptal = true; };
  }, []);

  // FCM mesaj dinleyici
  useEffect(() => {
    const setupFCMListener = async () => {
      try {
        const platform = typeof window !== 'undefined' && window.Capacitor && (window.Capacitor.isNativePlatform ? window.Capacitor.isNativePlatform() : !!window.Capacitor.getPlatform);
        
        if (platform) {
          console.log('📱 Android platform tespit edildi, FCM dinleyici kuruluyor...');
          
          // Capacitor Push Notifications plugin'ini kullan
          try {
            const { PushNotifications } = await import('@capacitor/push-notifications');
            
            // Bildirim izinlerini kontrol et
            const permissionState = await PushNotifications.checkPermissions();
            console.log('🔐 Bildirim izinleri:', permissionState);
            
            // Bildirim alındığında (uygulama açıkken)
            PushNotifications.addListener('pushNotificationReceived', (notification) => {
              console.log('🔔 Bildirim alındı (uygulama açık):', notification);
              // Sistem bildirimi otomatik olarak gösterilir
              // Ek olarak toast mesajı da göster
              toast.success(notification.title || 'Yeni bildirim');
            });
            
            // Bildirime tıklandığında
            PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
              console.log('👆 Bildirime tıklandı:', notification);
            });
            
            // Token alındığında
            PushNotifications.addListener('registration', (token) => {
              console.log('🎫 FCM Token alındı:', token.value);
            });
            
            // Token hatası
            PushNotifications.addListener('registrationError', (error) => {
              console.error('❌ FCM Token hatası:', error);
            });
            
            console.log('✅ FCM dinleyiciler başarıyla kuruldu');
          } catch (error) {
            console.error('❌ Capacitor Push Notifications hatası:', error);
            
            // Fallback olarak eski FCM dinleyici
            const messageListener = await onMessageListener();
            messageListener.then((payload) => {
              console.log('FCM mesaj alındı:', payload);
              toast.success(payload.notification?.title || 'Yeni bildirim');
            }).catch((err) => {
              console.error('FCM dinleyici hatası:', err);
            });
          }
        }
      } catch (error) {
        console.error('FCM dinleyici kurulum hatası:', error);
      }
    };

    setupFCMListener();
  }, []);

  // Push subscription'ı login sırasında yapılacak
  // eslint-disable-next-line react-hooks/exhaustive-deps

  /**
   * Basarili kimlik dogrulamasi sonrasi ortak oturum acma adimlari.
   *
   * Ayni akis uc yerden tetikleniyor (klasik giris, 2FA ikinci adimi, self
   * servis kayit/davet); kopyalanirsa biri guncellenip digeri unutulur.
   *
   * @param {object} veri Sunucunun oturum yaniti (token + user + tenant)
   * @param {string} basariMesaji Kullaniciya gosterilecek toast metni
   */
  const oturumuBaslat = useCallback((veri, basariMesaji = 'Başarıyla giriş yapıldı!') => {
    // Access + refresh token'i kaydet ve global header'a ekle.
    // Sunucu artik kisa omurlu access token ile birlikte, iptal edilebilir
    // bir refresh token doner; ikisi de saklanmali.
    storeSession(veri);
    setMustChangePassword(veri.must_change_password === true);
    localStorage.setItem('lastLoginTime', Date.now().toString());
    if (veri.tenant && veri.tenant.slug) {
      setTenant(veri.tenant);
    }
    setUser(veri.user);
    if (basariMesaji) toast.success(basariMesaji);

    // Push subscription'ı kısa bir gecikme ile yap
    setTimeout(() => {
      ensurePushSubscription();
    }, 1000);
  }, [ensurePushSubscription]);

  /**
   * Giris.
   *
   * DONUS DEGERI HER ZAMAN BIR NESNEDIR (eski true/false degil):
   *   { ok: true }                                  -> oturum acildi
   *   { ok: false, tenantSecimi: [{slug,name}] }    -> firma secimi gerekli
   *   { ok: false, ikinciAdim: '2fa', pendingToken } -> 2FA kodu gerekli
   *   { ok: false }                                 -> hata (toast gosterildi)
   *
   * @param {string} username Kullanici adi VEYA e-posta
   * @param {string} password Sifre
   * @param {{tenantSlug?: string}} opts Firma secimi yapildiysa slug
   */
  const login = async (username, password, { tenantSlug } = {}) => {
    const reqId = generateRequestId();

    const attemptLogin = async () => {
      // Lokal/Network geliştirme için dinamik baz URL kullan
      let currentBaseURL = getApiBaseUrlDynamic();
      const isRelative = !currentBaseURL || currentBaseURL === '' || currentBaseURL === 'undefined';

      // Axios baseURL'i set et
      axios.defaults.baseURL = isRelative ? '' : currentBaseURL;

      const loginUrl = `/api/auth/login`;
      
      // Detaylı debug bilgisi
      console.log('🔐 === LOGIN ATTEMPT DEBUG ===');
      console.log('📍 Base URL:', (isRelative ? '(relative via proxy)' : currentBaseURL) || '(empty - relative)');
      console.log('🔗 Full Login URL:', loginUrl);
      console.log('🌐 NODE_ENV:', process.env.NODE_ENV);
      console.log('📱 Window location:', typeof window !== 'undefined' ? window.location.href : 'N/A');
      console.log('🔧 Axios baseURL:', axios.defaults.baseURL || '(empty)');
      console.log('👤 Username:', username);
      
              // Backend sunucusunun çalışıp çalışmadığını kontrol et (retry mekanizması ile)
        let healthCheckSuccess = false;
        const healthUrl = isRelative ? '/api/health' : `${currentBaseURL.replace(/\/+$/,'')}/api/health`;
        const maxHealthRetries = 3;
        const healthRetryDelay = 1000; // 1 saniye
        
        for (let retry = 0; retry < maxHealthRetries; retry++) {
          try {
            const healthCheck = await axios.get(healthUrl, { 
              timeout: 8000, // 8 saniye timeout (sunucu başlatma için yeterli süre)
              validateStatus: (status) => status < 500 // 4xx hataları normal, 5xx sunucu hatası
            });
            console.log('✅ Backend sunucu çalışıyor:', healthCheck.status, healthUrl);
            healthCheckSuccess = true;
            break; // Başarılı oldu, retry döngüsünden çık
          } catch (healthError) {
            const isNetErr = healthError.code === 'ECONNREFUSED' || healthError.code === 'ERR_NETWORK' || healthError.message?.includes('Network Error');
            
            // Son deneme değilse ve network error ise retry yap
            if (retry < maxHealthRetries - 1 && isNetErr) {
              console.warn(`⚠️ Health check başarısız (${retry + 1}/${maxHealthRetries}), ${healthRetryDelay}ms sonra tekrar deneniyor...`);
              await new Promise(resolve => setTimeout(resolve, healthRetryDelay));
              continue;
            }
            
            // localhost -> 127.0.0.1 fallback'i YALNIZCA yerel gelistirmede.
            //
            // Burasi basarili olunca axios.defaults.baseURL KALICI olarak
            // http://127.0.0.1:5000'e cekiliyordu. Uretimde (veya mutlak
            // baseURL'li native kurulumda) bu, kullanicinin tum trafigini
            // sifresiz bir yerel adrese yonlendirebilirdi.
            const yerelGelistirme =
              process.env.NODE_ENV === 'development' &&
              typeof window !== 'undefined' &&
              ['localhost', '127.0.0.1'].includes(window.location.hostname);

            if (isNetErr && !isRelative && yerelGelistirme && retry === maxHealthRetries - 1) {
              console.warn('❌ Health check network error, 127.0.0.1 fallback deneniyor...');
              const fallbackBase = 'http://127.0.0.1:5000';
              try {
                const healthCheck2 = await axios.get(`${fallbackBase}/api/health`, {
                  timeout: 5000,
                  validateStatus: s => s < 500
                });
                console.log('✅ Fallback health OK:', healthCheck2.status, fallbackBase);
                axios.defaults.baseURL = fallbackBase;
                healthCheckSuccess = true;
                break;
              } catch (fallbackErr) {
                console.error('❌ Backend sunucu çalışmıyor (localhost ve 127.0.0.1 başarısız)!');
                // Fallback de başarısız oldu, ama login'i denemeye devam et
                // Çünkü bazen health check başarısız olabilir ama login çalışabilir
                console.warn('⚠️ Health check başarısız, ancak login denenmeye devam edilecek...');
              }
            } else if (retry === maxHealthRetries - 1) {
              // Son deneme başarısız oldu, ama login'i denemeye devam et
              console.warn('⚠️ Health check hatası (devam ediliyor - login denenebilir):', healthError.message);
            }
          }
        }
        
        // Health check başarısız olsa bile login'i denemeye devam et
        // Çünkü bazen health check başarısız olabilir ama login çalışabilir
        if (!healthCheckSuccess) {
          console.warn('⚠️ Health check başarısız oldu, ancak login denenmeye devam edilecek...');
        }

        // Add better error handling for network issues
        try {
          const response = await axios.post(loginUrl, { username, password }, {
            timeout: 20000, // 20 second timeout (increased for Railway cold start)
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              // Ayni kullanici adi birden fazla firmada olabilir. Kullanici
              // firmayi sectiginde sunucu kiraciyi kullanici adindan
              // cozmesin diye acikca bildiriyoruz (bkz. resolveTenantForLogin).
              ...(tenantSlug ? { 'X-Tenant': tenantSlug } : {})
              // User-Agent ve Origin header'ları browser tarafından güvenlik nedeniyle set edilemez
            },
            validateStatus: (status) => {
              // Sadece başarılı (2xx) response'ları "resolve" et.
              // 401/403 gibi hataları catch'e düşürerek yanlış "başarılı giriş" toast'ını engelliyoruz.
              return status >= 200 && status < 300;
            }
          });
        
        console.log('✅ Response status:', response.status);
        console.log('✅ Response data keys:', Object.keys(response.data || {}));
        
        return response;
      } catch (error) {
        console.error('❌ === LOGIN ERROR DEBUG ===');
        console.error('❌ Error message:', error.message);
        console.error('❌ Error code:', error.code);
        console.error('❌ Response status:', error.response?.status);
        console.error('❌ Response statusText:', error.response?.statusText);
        console.error('❌ Response data:', error.response?.data);
        console.error('❌ Request URL:', error.config?.url);
        console.error('❌ Request baseURL:', error.config?.baseURL);
        console.error('❌ Full URL:', (error.config?.baseURL || '') + (error.config?.url || ''));
        console.error('❌ Current Base URL (attemptLogin):', currentBaseURL);
        console.error('❌ Login URL (attemptLogin):', loginUrl);
        console.error('❌ Axios default baseURL:', axios.defaults.baseURL);
        console.error('❌ Error is Network Error?', error.message?.includes('Network Error') || error.code === 'ERR_NETWORK');
        console.error('❌ Error details:', JSON.stringify({
          message: error.message,
          code: error.code,
          status: error.response?.status,
          responseData: error.response?.data,
          config: {
            url: error.config?.url,
            baseURL: error.config?.baseURL,
            method: error.config?.method
          }
        }, null, 2));
        
        // 503 Service Unavailable - Railway overload/rate limit
        if (error.response?.status === 503) {
          throw {
            ...error,
            is503: true,
            message: 'Sunucu geçici olarak kullanılamıyor. Lütfen birkaç saniye bekleyip tekrar deneyin.'
          };
        }
        
        if (error.code === 'ECONNREFUSED' || error.code === 'ENOTFOUND') {
          throw new Error(`Sunucuya bağlanılamıyor (${error.code}). URL: ${loginUrl}`);
        }
        if (error.code === 'ECONNABORTED') {
          throw new Error('Bağlantı zaman aşımına uğradı. Lütfen tekrar deneyin.');
        }
        throw error;
      }
    };

    const shouldRetry = (err) => {
      const status = err.response?.status;
      // 503 (Service Unavailable) ve network hatalarını retry et
      if (status === 503 || err.is503) {
        return true; // Railway overload durumunda retry
      }
      return !status || status >= 500; // Network error veya diğer 5xx
    };

    try {
      // API URL'i tekrar kontrol et ve güncelle
      updateAxiosBaseURL();
      
      console.log('🔐 Login attempt for:', username, 'reqId:', reqId);
      console.log('🌐 API Base URL:', axios.defaults.baseURL);
      console.log('🌐 NODE_ENV:', process.env.NODE_ENV);
      console.log('🌐 Window location:', typeof window !== 'undefined' ? window.location.href : 'N/A');
      console.log('🔗 Full login URL:', `${axios.defaults.baseURL}/api/auth/login`);

      let response;
      let attempt = 0;
      const maxAttempts = 5; // 503 için daha fazla deneme
      const baseDelayMs = 2000; // 2 saniye başlangıç delay

      while (attempt < maxAttempts) {
        try {
          response = await attemptLogin();
          
          // 503 hatası alırsak retry et
          if (response.status === 503) {
            throw {
              response: { status: 503 },
              is503: true,
              message: 'Sunucu geçici olarak kullanılamıyor.'
            };
          }
          
          break; // success
        } catch (err) {
          attempt += 1;
          const is503 = err.response?.status === 503 || err.is503;
          console.warn(`Login denemesi ${attempt}/${maxAttempts} hata:`, err.message || err.response?.status, is503 ? '(503 - Retrying...)' : '');
          
          if (attempt >= maxAttempts || !shouldRetry(err)) {
            throw err;
          }
          
          // 503 için exponential backoff (2s, 4s, 8s, 16s)
          const backoff = is503 
            ? baseDelayMs * Math.pow(2, attempt - 1)
            : baseDelayMs * attempt;
          
          console.log(`⏳ ${Math.round(backoff/1000)} saniye bekleniyor...`);
          await new Promise((res) => setTimeout(res, backoff));
        }
      }

      const yanit = response?.data || {};

      // 2FA etkin hesap: sunucu token/user DONDURMEZ, yalnizca 5 dk omurlu bir
      // pending_token verir. Oturum ancak verify2fa basarili olunca acilir.
      if (yanit.two_factor_required && yanit.pending_token) {
        console.log('🔐 2FA gerekli, ikinci adıma geçiliyor. reqId:', reqId);
        return { ok: false, ikinciAdim: '2fa', pendingToken: yanit.pending_token };
      }

      // Kurumsal güvenlik/UX: Başarılı login için kesin olarak token ve user olmalı.
      if (!yanit.token || !yanit.user) {
        throw {
          response: { status: response?.status, data: yanit },
          message: yanit.error || 'Giriş başarısız'
        };
      }

      console.log('✅ Login successful, user:', yanit.user, 'reqId:', reqId);
      oturumuBaslat(yanit);
      return { ok: true };
    } catch (error) {
      // Ayni kullanici adi birden fazla firmada: bu bir HATA DEGIL, ek bir
      // adim. Toast/hata logu uretmeden Login.js'e firma listesini donuyoruz;
      // secim yapilinca ayni istek X-Tenant basligiyla tekrarlanacak.
      if (error.response?.status === 409 && error.response?.data?.code === 'TENANT_AMBIGUOUS') {
        const firmalar = Array.isArray(error.response.data.tenants) ? error.response.data.tenants : [];
        console.log('🏢 Birden fazla firma bulundu, seçim isteniyor. reqId:', reqId);
        return { ok: false, tenantSecimi: firmalar };
      }

      const details = {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        config: {
          url: error.config?.url,
          baseURL: error.config?.baseURL,
          method: error.config?.method
        },
        code: error.code,
        stack: error.stack
      };
      console.error('❌ Login error details:', details);

      // Sunucuya ayrıntılı log gönder
      try {
        await axios.post('/api/log-error', {
          error: `Login failed (reqId=${reqId})` ,
          deviceInfo: {
            type: 'login_error',
            username,
            platform: navigator.platform,
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString(),
            reqId
          }
        });
      } catch (_) {}
      
      let message = `Giriş yapılamadı (Kod: ${reqId})`;
      
      // Detaylı hata analizi
      console.error('❌ Login Error Analysis:', {
        code: error.code,
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        responseData: error.response?.data,
        baseURL: axios.defaults.baseURL,
        url: error.config?.url,
        fullUrl: error.config?.baseURL + error.config?.url
      });
      
      // 503 Service Unavailable - Railway overload
      if (error.response?.status === 503 || error.is503) {
        message = `Sunucu şu anda yoğun. Lütfen birkaç saniye bekleyip tekrar deneyin. (Kod: ${reqId})`;
      } else if (error.code === 'NETWORK_ERROR' || error.message?.includes('Network Error') || error.message?.includes('ERR_NETWORK')) {
        message = `Ağ bağlantı hatası. Lütfen internet bağlantınızı kontrol edin. (Kod: ${reqId})`;
      } else if (error.code === 'ECONNREFUSED' || error.code === 'ENOTFOUND') {
        message = `Sunucuya bağlanılamıyor. API URL: ${axios.defaults.baseURL || '(ayarlanmamış)'} (Kod: ${reqId})`;
      } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        message = `Bağlantı zaman aşımına uğradı. (Kod: ${reqId})`;
      } else if (error.response?.status === 401) {
        message = 'Kullanıcı adı veya şifre hatalı';
      } else if (error.response?.status === 429) {
        message = error.response?.data?.error || `Çok fazla giriş denemesi. Biraz sonra tekrar deneyin. (Kod: ${reqId})`;
      } else if (error.response?.status >= 500) {
        message = `Sunucu hatası (${error.response?.status}). Lütfen daha sonra tekrar deneyin. (Kod: ${reqId})`;
      } else if (error.response?.data?.error) {
        message = `${error.response.data.error} (Kod: ${reqId})`;
      } else if (!error.response) {
        // No response - network error
        message = `Sunucuya bağlanılamıyor. API: ${axios.defaults.baseURL || '(ayarlanmamış)'} (Kod: ${reqId})`;
      }
      
      console.error('❌ Final error message:', message);
      toast.error(message);
      return { ok: false };
    }
  };

  /**
   * 2FA ikinci adimi. login() {ikinciAdim:'2fa'} donduginde cagrilir.
   * @returns {{ok:boolean}}
   */
  const verify2fa = async (pendingToken, code) => {
    try {
      const response = await axios.post('/api/auth/verify-2fa', {
        pending_token: pendingToken,
        code: String(code || '').trim(),
      }, { skipAuthRefresh: true });

      const yanit = response?.data || {};
      if (!yanit.token || !yanit.user) {
        toast.error(yanit.error || 'Doğrulama başarısız');
        return { ok: false };
      }

      oturumuBaslat(yanit);
      return { ok: true };
    } catch (error) {
      const kod = error.response?.data?.code;
      const mesaj = error.response?.data?.error
        || error.response?.data?.errors?.[0]?.msg
        || 'Doğrulama kodu doğrulanamadı';
      if (!interceptorZatenBildirdiMi(error.response?.status)) toast.error(mesaj);
      // Pending token'in suresi dolduysa kullanici bastan giris yapmali —
      // Login.js bu bayrakla ilk adima doner.
      return { ok: false, sureDoldu: kod === 'PENDING_2FA_INVALID' };
    }
  };

  /**
   * Self servis firma kaydi. Sunucu 201 ile birlikte oturum acan bir yanit
   * doner (token + user + tenant); dondugu icin kullaniciyi tekrar giris
   * ekranina gondermeye gerek yok.
   *
   * @param {object} veri {company_name, full_name, email, username, password, slug, kvkk_onay, terms_onay, website}
   * @returns {{ok:boolean, otomatikGiris?:boolean, hata?:string}}
   */
  const signup = async (veri) => {
    try {
      const response = await axios.post('/api/signup', veri, { skipAuthRefresh: true });
      const yanit = response?.data || {};

      if (yanit.token && yanit.user) {
        oturumuBaslat(yanit, 'Hesabınız oluşturuldu. Hoş geldiniz!');
        return { ok: true, otomatikGiris: true };
      }

      // Bal kupu doldurulduysa sunucu istegi sessizce yutar ve yalnizca
      // {message} doner — bota basarili gorunur, kayit olusmaz.
      return { ok: true, otomatikGiris: false };
    } catch (error) {
      const mesaj = error.response?.data?.error
        || error.response?.data?.errors?.[0]?.msg
        || 'Hesap oluşturulamadı. Lütfen tekrar deneyin.';
      if (!interceptorZatenBildirdiMi(error.response?.status)) toast.error(mesaj);
      return { ok: false, hata: mesaj };
    }
  };

  /**
   * Daveti kabul et. Sunucu kullaniciyi olusturup dogrudan oturum acar.
   *
   * NOT: /api/signup/invitations/accept 'username' alanini da ZORUNLU tutar
   * (min 3 karakter) — davet dogrulama yanitindaki e-posta/rol bilgisi tek
   * basina yetmez.
   *
   * @param {object} veri {token, full_name, username, password}
   * @param {object} [tenantBilgisi] Davet dogrulamasindan gelen {slug,name};
   *   kabul yaniti yalnizca slug dondurdugu icin firma adini buradan tamamliyoruz.
   */
  const acceptInvitation = async (veri, tenantBilgisi) => {
    try {
      const response = await axios.post('/api/signup/invitations/accept', veri, { skipAuthRefresh: true });
      const yanit = response?.data || {};

      if (!yanit.token || !yanit.user) {
        const mesaj = yanit.error || 'Davet kabul edilemedi';
        toast.error(mesaj);
        return { ok: false, hata: mesaj };
      }

      oturumuBaslat({
        ...yanit,
        tenant: { ...(tenantBilgisi || {}), ...(yanit.tenant || {}) },
      }, 'Hesabınız oluşturuldu. Hoş geldiniz!');
      return { ok: true };
    } catch (error) {
      const mesaj = error.response?.data?.error
        || error.response?.data?.errors?.[0]?.msg
        || 'Davet kabul edilemedi';
      if (!interceptorZatenBildirdiMi(error.response?.status)) toast.error(mesaj);
      return { ok: false, hata: mesaj, durum: error.response?.status };
    }
  };

  /**
   * Sifre sifirlama baglantisi iste.
   * Sunucu hesap varligini SIZDIRMAMAK icin her durumda 200 doner; istemci de
   * ayni sekilde davranmali (var/yok ayrimi yapan mesaj gostermemeli).
   */
  const forgotPassword = async (email) => {
    try {
      const response = await axios.post('/api/auth/forgot-password', { email }, { skipAuthRefresh: true });
      return { ok: true, mesaj: response?.data?.message };
    } catch (error) {
      // 429 (saatte 5 istek) ve dogrulama hatalari kullaniciya gosterilmeli.
      const mesaj = error.response?.data?.error
        || error.response?.data?.errors?.[0]?.msg
        || 'İstek gönderilemedi. Lütfen daha sonra tekrar deneyin.';
      return { ok: false, hata: mesaj };
    }
  };

  /** E-postadaki token ile yeni sifre belirle */
  const resetPassword = async (token, yeniSifre) => {
    try {
      const response = await axios.post('/api/auth/reset-password', {
        token,
        new_password: yeniSifre,
      }, { skipAuthRefresh: true });
      return { ok: true, mesaj: response?.data?.message };
    } catch (error) {
      const mesaj = error.response?.data?.error
        || error.response?.data?.errors?.[0]?.msg
        || 'Şifre güncellenemedi.';
      return { ok: false, hata: mesaj };
    }
  };

  const logout = async ({ allDevices = false } = {}) => {
    try {
      // Refresh token'i sunucuya gonder ki GERCEKTEN iptal edilsin.
      // Onceden yalnizca istemcideki token siliniyordu; sunucudaki oturum
      // gecerli kalmaya devam ediyordu.
      await axios.post('/api/auth/logout', {
        refresh_token: localStorage.getItem('refreshToken') || undefined,
        all_devices: allDevices,
      }, { skipAuthRefresh: true });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearStoredSession();
      setUser(null);
      setTenant(null);
      toast.success(allDevices ? 'Tüm cihazlardan çıkış yapıldı' : 'Başarıyla çıkış yapıldı');
    }
  };

  const refreshToken = async () => {
    try {
      const token = await runTokenRefresh();
      return token;
    } catch (error) {
      setUser(null);
      throw error;
    }
  };

  const changePassword = async (currentPassword, newPassword, confirmPassword) => {
    try {
      // 1) Step-up doğrulama için OTP iste
      await axios.post('/api/auth/change-password/request', { currentPassword });
      const otp = window.prompt('E-posta adresinize gelen doğrulama kodunu giriniz:');
      if (!otp) {
        toast.error('Doğrulama kodu gerekli');
        return false;
      }
      // 2) OTP ile şifreyi onayla
      await axios.post('/api/auth/change-password/confirm', {
        otp,
        newPassword,
        confirmPassword
      });
      toast.success('Şifre başarıyla değiştirildi');
      return true;
    } catch (error) {
      const detail = error.response?.data?.errors?.[0] || null;
      const message = error.response?.data?.error || detail?.msg || 'Şifre değiştirilemedi';
      toast.error(message);
      return false;
    }
  };

  const updateProfile = async (updates) => {
    try {
      const response = await axios.put('/api/auth/profile', updates);
      const updatedUser = response.data?.user;
      if (updatedUser) {
        setUser(updatedUser);
      }
      toast.success('Profil güncellendi');
      return true;
    } catch (error) {
      const message = error.response?.data?.error || error.response?.data?.errors?.[0]?.msg || 'Profil güncellenemedi';
      toast.error(message);
      return false;
    }
  };

  const value = {
    user,
    tenant,
    loading,
    login,
    verify2fa,
    signup,
    acceptInvitation,
    forgotPassword,
    resetPassword,
    logout,
    refreshToken,
    changePassword,
    mustChangePassword,
    setMustChangePassword,
    updateProfile,
    ensurePushSubscription
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 